import jsonLogic, { RulesLogic } from 'json-logic-js';
import { transform } from 'lodash';
import { isObject, snakeCase } from 'lodash/fp';

const formatArrayValues = (values: any[]) => {
  if (values.every((v) => typeof v === 'string')) {
    return values?.map((v) => v.toLowerCase());
  } else {
    return values;
  }
};

const formatAnyValues = (values: any) => {
  if (Array.isArray(values)) {
    return formatArrayValues(values);
  } else {
    return values?.toString().toLowerCase();
  }
};

// Support our custom operations that are not by default supported by the
// json logic library

// WorkflowOperatorKey.ContainsAnySubstring
jsonLogic.add_operation('containsanysubstring', (a: string, b: string[]) => {
  return formatArrayValues(b).some((substr: string) => {
    return formatAnyValues(a).includes(substr);
  });
});

// WorkflowOperatorKey.ContainsNoSubstring
jsonLogic.add_operation('containsnosubstring', (a: string, b: string[]) => {
  return formatArrayValues(b).every((substr: string) => {
    return !formatAnyValues(a).includes(substr);
  });
});

// WorkflowOperatorKey.StartsWith
jsonLogic.add_operation('startswith', (a: string, b: string[]) => {
  return formatArrayValues(b).some((startstr: string) => {
    return formatAnyValues(a).some((str: string) => {
      return str.startsWith(startstr);
    });
  });
});

// WorkflowOperatorKey.In
jsonLogic.add_operation('in', (a: any, b: any[]) => {
  return formatArrayValues(b).indexOf(formatAnyValues(a)) !== -1;
});

//  WorkflowOperatorKey.NotIn,
jsonLogic.add_operation('nin', (a: any, b: any[]) => {
  return formatArrayValues(b).indexOf(formatAnyValues(a)) === -1;
});

// WorkflowOperatorKey.IsBlank
jsonLogic.add_operation('isblank', (a: any) => {
  return a == null;
});

// WorkflowOperatorKey.IsNotBlank
jsonLogic.add_operation('isnotblank', (a: any) => {
  return a != null;
});

// WorkflowOperatorKey.Contains
jsonLogic.add_operation('contains', (a: any, b: any) => {
  const formattedA = formatAnyValues(a);
  const formattedB = formatAnyValues(b);

  // intersection of two arrays or strings
  if (Array.isArray(formattedA) && Array.isArray(formattedB)) {
    return formattedA.some((v) => formattedB.includes(v));
  } else if (typeof formattedA === 'string' && typeof formattedB === 'string') {
    return (formattedA.match(new RegExp('[' + formattedB + ']', 'g')) || []).join('').length > 0;
  }
});

// WorkflowOperatorKey.IncludesAll
jsonLogic.add_operation('includesall', (a: any[], b: any[]) => {
  return formatArrayValues(b).every((el: any) => formatArrayValues(a).includes(el));
});

// WorkflowOperatorKey.IncludesNone
jsonLogic.add_operation('includesnone', (a: any[], b: any[]) => {
  return formatArrayValues(b).every((el: any) => !formatArrayValues(a).includes(el));
});

// WorkflowOperatorKey.IncludesNotAll
jsonLogic.add_operation('includesnotall', (a: any[], b: any[]) => {
  return formatArrayValues(b).some((el: any) => !formatArrayValues(a).includes(el));
});

// WorkflowOperatorKey.IncludesOnly
jsonLogic.add_operation('includesonly', (a: any[], b: any[]) => {
  return formatArrayValues(a).every((el: any) => formatArrayValues(b).includes(el));
});

// WorkflowOperatorKey.IncludesExactly
jsonLogic.add_operation('includesexactly', (a: any[], b: any[]) => {
  return (
    formatArrayValues(a).every((el: any) => formatArrayValues(b).includes(el)) &&
    formatArrayValues(b).every((el: any) => formatArrayValues(a).includes(el))
  );
});

// WorkflowOperatorKey.IncludesNotExactly
jsonLogic.add_operation('includesnotexactly', (a: any[], b: any[]) => {
  return (
    formatArrayValues(b).some((el: any) => !formatArrayValues(a).includes(el)) ||
    formatArrayValues(a).some((el: any) => !formatArrayValues(b).includes(el))
  );
});

// WorkflowOperatorKey.Equals
jsonLogic.add_operation('=', (a: string, b: string) => {
  // disabling triple equals so that behavior is equivalent to default == operation
  // eslint-disable-next-line eqeqeq
  return a == b;
});

// WorkflowOperatorKey.StartsWith (startswith)
jsonLogic.add_operation('startswith', (a: string, b: string) => {
  return formatAnyValues(a).startsWith(formatAnyValues(b));
});

/*
  runJsonLogic is a wrapper for jsonLogic.apply which takes in
  rules and data, and returns a boolean value for whether
  the rules pass or not
  See https://jsonlogic.com/ for more details
*/
export const runJsonLogic = (rules: RulesLogic, data: unknown) => {
  return jsonLogic.apply(rules, data);
};

// Returns a new object with any keys converted to snake case. Works on nested objects.
export const convertKeysToSnakeCase = (obj: any) => {
  return transform(obj, (result: Record<string, any>, value: any, key: string) => {
    const newKey = snakeCase(key) as string;
    result[newKey] = isObject(value) ? convertKeysToSnakeCase(value) : value;
  });
};

// Returns a unique array of WF operators in a JsonLogic rule
export const getOperators = (logic: Record<string, any>) => {
  const operators: string[] = [];

  if (jsonLogic.is_logic(logic)) {
    const operator = jsonLogic.get_operator(logic);
    // type casting needed due to mismatch from json-logic-js types
    let values = (logic as Record<string, any>)[operator];
    if (!Array.isArray(values)) {
      values = [values];
    }

    if (operator !== 'and' && operator !== 'or') {
      operators.push(operator);
    } else {
      values.forEach((innerLogic: Record<string, any>) => {
        operators.push(...getOperators(innerLogic));
      });
    }
  }

  return [...new Set(operators)];
};
